import React, { useState } from "react";
import {
    Box,
    Typography,
    Stack,
    Chip,
    Divider,
    Breadcrumbs,
    Tooltip,
    Button,
    IconButton,
    Menu,
    MenuItem,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { Link } from "react-router-dom";

// Icons
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import PaletteIcon from "@mui/icons-material/Palette";
import SpaceDashboardOutlinedIcon from "@mui/icons-material/SpaceDashboardOutlined";

// Context
import { useThemeMode } from "../../contexts/ThemeContext";

const SWATCHES = {
    merubot: ["#00C8FF", "#8B5CF6"],
    midnightEmber: ["#F97316", "#EF4444"],
    forestSage: ["#22C55E", "#84CC16"],
    roseQuartz: ["#F472B6", "#A855F7"],
};

/**
 * CustomHeader
 *
 * Props:
 * title         – string    – Main page title
 * subtitle      – string    – Secondary text displayed below title
 * icon          – node      – Custom left-side icon (defaults to Dashboard icon)
 * breadcrumbs   – array     – Array of { label, path } for dynamic routing. First link defaults to Dashboard if omitted.
 * buttonText    – string    – Main action button text
 * onButtonClick – fn        – Action button handler
 * buttonIcon    – node      – Optional start icon for the action button
 * customChips   – array     – Array of { label, color/dot } to display next to the subtitle
 */
const CustomHeader = ({
    title = "Dashboard",
    subtitle = "",
    icon,
    breadcrumbs = [],
    buttonText = "",
    onButtonClick,
    buttonIcon,
    customChips = [],
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    // Theme context
    const {
        mode = "light",
        toggleTheme,
        themeKey,
        setThemeKey,
        themeOptions,
    } = useThemeMode();

    // Menu state
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleOpenMenu = (e) => setAnchorEl(e.currentTarget);
    const handleCloseMenu = () => setAnchorEl(null);

    const handleThemeSelect = (key) => {
        setThemeKey(key);
        handleCloseMenu();
    };

    // Default breadcrumb setup (Always starts with Dashboard)
    const displayBreadcrumbs =
        breadcrumbs.length > 0
            ? breadcrumbs
            : [{ label: "Dashboard", path: "/recruiter/dashboard" }];

    return (
        <Box
            sx={{
                px: { xs: 2, md: 3 },
                py: 1.25, // Reduced padding for a slimmer profile
                mb: 3,
                position: "sticky",
                top: 0,
                zIndex: 1100,
                background: isDark
                    ? alpha(theme.palette.background.default, 0.8)
                    : alpha(theme.palette.background.paper, 0.85),
                backdropFilter: "blur(12px)",
                boxShadow: isDark ? "0 4px 15px rgba(0,0,0,0.3)" : theme.shadows[1],
                borderBottom: `1px solid ${alpha(theme.palette.divider, isDark ? 0.1 : 0.4)}`,
            }}
        >
            {/* 1. Top Breadcrumbs Row - Tightened Margin */}
            <Breadcrumbs
                separator={<NavigateNextIcon sx={{ fontSize: 12, color: "text.disabled" }} />}
                sx={{ mb: 0.25 }}
            >
                {displayBreadcrumbs.map((crumb, index) => {
                    const isLast = index === displayBreadcrumbs.length - 1;
                    return isLast ? (
                        <Typography key={index} fontSize="0.7rem" color="text.primary" fontWeight={600}>
                            {crumb.label}
                        </Typography>
                    ) : (
                        <Typography
                            key={index}
                            component={Link}
                            to={crumb.path}
                            fontSize="0.7rem"
                            color="text.secondary"
                            sx={{ textDecoration: "none", "&:hover": { color: theme.palette.primary.main } }}
                        >
                            {crumb.label}
                        </Typography>
                    );
                })}
            </Breadcrumbs>

            {/* 2. Main Header Row */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center", // Align items to center for a more compact row
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                }}
            >
                {/* Left Side – Icon + Title + Subtitle */}
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                        sx={{
                            width: 36, // Shrunk from 44
                            height: 36,
                            borderRadius: "10px",
                            bgcolor: alpha(theme.palette.primary.main, isDark ? 0.15 : 0.1),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
                        }}
                    >
                        {icon || <SpaceDashboardOutlinedIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />}
                    </Box>

                    <Box>
                        <Typography
                            variant="h6" // Shrunk from h5
                            sx={{
                                fontWeight: 700,
                                fontSize: "1.1rem",
                                color: isDark ? theme.palette.primary.light : theme.palette.text.primary,
                                lineHeight: 1.1,
                                letterSpacing: "-0.01em",
                            }}
                        >
                            {title}
                        </Typography>

                        {(subtitle || customChips.length > 0) && (
                            <Stack direction="row" alignItems="center" spacing={1} mt={0.25} flexWrap="wrap">
                                {subtitle && (
                                    <Typography fontSize="0.7rem" color="text.secondary" fontWeight={500}>
                                        {subtitle}
                                    </Typography>
                                )}

                                {customChips.map((chip, idx) => (
                                    <Chip
                                        key={idx}
                                        size="small"
                                        label={chip.label}
                                        sx={{
                                            fontSize: "0.65rem",
                                            fontWeight: 600,
                                            height: 18, // Slimmer chips
                                            borderRadius: "4px",
                                            bgcolor: alpha(chip.color || theme.palette.primary.main, 0.1),
                                            color: chip.color || theme.palette.primary.main,
                                        }}
                                    />
                                ))}
                            </Stack>
                        )}
                    </Box>
                </Stack>

                {/* Right Side – Controls & Actions */}
                <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                    
                    {/* Theme Toggles */}
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Tooltip title="Select Theme" arrow>
                            <IconButton
                                onClick={handleOpenMenu}
                                size="small"
                                sx={{
                                    width: 32,
                                    height: 32,
                                    border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.2 : 0.6)}`,
                                    borderRadius: "8px",
                                    color: theme.palette.text.primary,
                                    "&:hover": { background: alpha(theme.palette.primary.main, 0.1) },
                                }}
                            >
                                <PaletteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>

                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleCloseMenu}
                            PaperProps={{
                                sx: {
                                    mt: 1,
                                    background: isDark ? "#1A1D2E" : "#FFF",
                                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                    boxShadow: theme.shadows[6],
                                    borderRadius: "10px",
                                },
                            }}
                        >
                            {Object.entries(themeOptions ?? {}).map(([key, value]) => {
                                const label = value.label;
                                const [c1, c2] = SWATCHES[key] ?? ["#888", "#aaa"];
                                const isActive = key === themeKey;
                                return (
                                    <MenuItem
                                        key={key}
                                        selected={isActive}
                                        onClick={() => handleThemeSelect(key)}
                                        sx={{ gap: 1.5, py: 0.75, px: 2 }}
                                    >
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: "50%",
                                                background: `linear-gradient(135deg, ${c1}, ${c2})`,
                                                boxShadow: isActive ? `0 0 6px ${c1}` : "none",
                                            }}
                                        />
                                        <Typography variant="body2" sx={{ fontSize: "0.8rem", fontWeight: isActive ? 600 : 400 }}>
                                            {label}
                                        </Typography>
                                    </MenuItem>
                                );
                            })}
                        </Menu>

                        <Tooltip title={mode === "dark" ? "Switch to light" : "Switch to dark"} arrow>
                            <IconButton
                                onClick={toggleTheme}
                                size="small"
                                sx={{
                                    width: 32,
                                    height: 32,
                                    color: isDark ? theme.palette.warning.light : theme.palette.text.secondary,
                                    border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.2 : 0.6)}`,
                                    borderRadius: "8px",
                                    "&:hover": {
                                        background: alpha(theme.palette.primary.main, 0.1),
                                        color: theme.palette.primary.main,
                                    },
                                }}
                            >
                                {mode === "dark" ? <LightModeIcon sx={{ fontSize: 16 }} /> : <DarkModeIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                        </Tooltip>
                    </Stack>

                    {buttonText && <Divider orientation="vertical" flexItem sx={{ mx: 0.25, my: 0.5 }} />}

                    {/* Action Button */}
                    {buttonText && (
                        <Button
                            variant="contained"
                            onClick={onButtonClick}
                            startIcon={buttonIcon}
                            sx={{
                                height: 32, // Slimmer button
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                textTransform: "none",
                                borderRadius: "8px",
                                px: 2.5,
                                background: isDark
                                    ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                                    : `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                                color: "#FFFFFF",
                                boxShadow: isDark
                                    ? `0 4px 12px 0 ${alpha(theme.palette.primary.main, 0.3)}`
                                    : theme.shadows[2],
                                "&:hover": {
                                    transform: "translateY(-1px)",
                                    boxShadow: isDark
                                        ? `0 6px 16px 0 ${alpha(theme.palette.primary.main, 0.4)}`
                                        : theme.shadows[4],
                                },
                            }}
                        >
                            {buttonText}
                        </Button>
                    )}
                </Stack>
            </Box>
        </Box>
    );
};

export default CustomHeader;

